import { computed, ErrorHandler, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { extractApiError } from '../utils/api-error';

@Injectable()
export class GlobalErrorHandlerService implements ErrorHandler {
  private readonly _consoleErrors = signal<string[]>([]);
  readonly consoleErrors = this._consoleErrors.asReadonly();

  /** Most recent error message, or null when the buffer is empty. */
  readonly lastError = computed<string | null>(() => {
    const errs = this._consoleErrors();
    return errs.length > 0 ? errs[errs.length - 1] : null;
  });

  private pushError(msg: string): void {
    this._consoleErrors.update(errs => {
      const next = [...errs, msg];
      return next.slice(-10);
    });
  }

  handleError(error: unknown): void {
    // HttpErrorResponse is NOT an Error instance; String() on it yields "[object Object]".
    const nonError =
      error instanceof HttpErrorResponse ? extractApiError(error) : String(error);
    const raw = error instanceof Error ? error.message : nonError;
    // After a new deployment, old lazy-loaded chunk URLs 404. Auto-reload recovers silently.
    if (raw.includes('dynamically imported module')) {
      globalThis.location.reload();
      return;
    }
    console.error('[GlobalErrorHandler]', error);
    let safe = raw.replace(/\n[\s\S]*/g, '').slice(0, 120);
    // Last-resort guard: never surface "[object Object]" or an empty message.
    if (!safe || safe === '[object Object]') {
      safe = 'Unexpected error';
    }
    this.pushError(safe);
  }

  dismiss(): void {
    this._consoleErrors.set([]);
  }
}
